import { z } from 'zod'

/**
 * Roles and permission vocabulary. The role -> permission MATRIX lives in
 * @hm/core/policy (it is domain logic and is exhaustively unit-tested there);
 * this module only defines the shared names, so the client can reason about a
 * permission list the server hands it.
 */

export const ROLES = ['super_admin', 'admin', 'seller', 'buyer'] as const
export type Role = (typeof ROLES)[number]
export const roleSchema = z.enum(ROLES)

export const ORG_TYPES = ['platform', 'seller', 'buyer'] as const
export type OrgType = (typeof ORG_TYPES)[number]
export const orgTypeSchema = z.enum(ORG_TYPES)

export const ADMIN_ROLES: readonly Role[] = ['super_admin', 'admin']
export function isAdminRole(role: Role): boolean {
  return role === 'super_admin' || role === 'admin'
}

/** The alias every counterparty sees in chat instead of an individual admin (D4). */
export const ADMIN_GROUP_ALIAS = 'Support Desk'

export const PERMISSIONS = [
  // People
  'org:create',
  'org:read',
  'org:update',
  'org:disable',
  'user:create',
  'user:read',
  'user:update',
  'user:disable',
  'user:reset_password',
  // Only the super admin may create or disable admin-group accounts.
  'admin_user:manage',

  // Catalog master
  'category:manage',
  'product:read',
  'product:create',
  'product:update',
  'product:merge',
  'product_proposal:create',
  'product_proposal:review',

  // Seller-side inventory
  'listing:manage_own',
  'listing:read_any',

  // Platform-side catalog and stock
  'admin_listing:read',
  'admin_listing:manage',
  'platform_stock:manage',
  'stock:read_own',
  'stock:read_any',
  'stock:adjust_own',

  // Buyer-facing catalog
  'catalog:browse',
  'cart:manage',

  // Deals, sell side
  'rfq:create',
  'rfq:read_own',
  'rfq:manage',
  'offer:respond',
  'order:read_own',
  'order:manage',

  // Deals, buy side. Sellers see product + quantity + city, never the customer (D4).
  'sourcing:manage',
  'sourcing:respond',
  'purchase_order:manage',
  'purchase_order:respond',

  // Money. Reading the position and recording that it moved are separate, because
  // "who owes what" is a daily question and "somebody paid" is an entry in a ledger.
  'ledger:read',
  'ledger:manage',

  // Bulk data. Reading a spreadsheet out is not the same risk as writing one in.
  'data:export',

  // Standing arrangements: what a particular customer pays.
  'price_book:manage',

  // Messaging
  'chat:participate',
  'chat:internal_note',

  // Oversight
  'notification:read',
  'report:read',
  'audit:read',
  'audit:verify',
  'settings:manage',
] as const

export type Permission = (typeof PERMISSIONS)[number]
export const permissionSchema = z.enum(PERMISSIONS)

export function hasPermission(granted: readonly string[], needed: Permission): boolean {
  return granted.includes(needed)
}

export const ROLE_LABELS: Record<Role, string> = {
  super_admin: 'Owner',
  admin: 'Staff',
  seller: 'Shop',
  buyer: 'Customer',
}

export const ORG_TYPE_LABELS: Record<OrgType, string> = {
  platform: 'Platform',
  seller: 'Supplier shop',
  buyer: 'Customer',
}
